/* global gdAlertify */
import { useState, useEffect } from "react";
import axiosInstance from "../../config/axiosConfig";
import { formatMoney } from "../../utils/utils";
import BuscarAuxiliar from "./BuscarAuxiliar";
import GdTable from "./GdTable";

const retencionSii = 0.855;

const getRutParts = (row) => {
    const rawRut = row.rut_paciente || row.rut || "";
    const rutText = String(rawRut).trim();

    if (row.dv_paciente || row.dv) {
        return {
            rut: rutText.replace(/\./g, "").replace(/-$/, ""),
            dv: row.dv_paciente || row.dv || ""
        };
    }

    const [rut, dv] = rutText.replace(/\./g, "").split("-");
    return {
        rut: rut || "",
        dv: dv || ""
    };
};

const normalizeEstadoBoleta = (estado) => {
    const value = String(estado || "").toLowerCase().trim();
    if (["emitida", "aprobada", "aprobado"].includes(value)) return "aprobada";
    if (["error", "rechazada", "rechazado"].includes(value)) return "rechazada";
    return value;
};

const getDatosBoletaFromLegacy = (datosSii, key) => {
    const text = String(datosSii || "");
    const match = text.match(new RegExp(`${key}:\\s*([^|]+)`, "i"));
    return match ? match[1].trim() : "";
};

const getTipoBoleta = (row) => {
    const tipo = row.tipo_boleta || row.tipoBoleta || "";
    if (tipo) return tipo === "tercero" ? "Tercero" : "Propia";

    return String(row.datos_sii || "").toLowerCase().includes("tercero") ? "Tercero" : "Propia";
};

const getRutBoleta = (row) => (
    row.rut_boleta ||
    row.rutBoleta ||
    getDatosBoletaFromLegacy(row.datos_sii, "RUT") ||
    row.rut_auxiliar ||
    ""
);

const ESTADO_LABELS = { aprobada: "Emitida", rechazada: "Error", "": "No Emitida" };

const withEstadoDerivado = (row) => {
    const estadoNormalizado = normalizeEstadoBoleta(row.boleta_estado);
    return {
        ...row,
        estado_normalizado: estadoNormalizado,
        estado_label: ESTADO_LABELS[estadoNormalizado] || "No Emitida"
    };
};

const getNombreBoleta = (row) => (
    row.nombre_boleta ||
    row.nombreBoleta ||
    getDatosBoletaFromLegacy(row.datos_sii, "Nombre") ||
    `${row.nombre_auxiliar || ""} ${row.apepat_auxiliar || ""} ${row.apemat_auxiliar || ""}`.trim()
);

const BoletasHonorarios = () => {
    const [periodo, setPeriodo] = useState(new Date().toISOString().slice(0, 7));
    const [boletas, setBoletas] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [file, setFile] = useState(null);
    const [auxiliarInfo, setAuxiliarInfo] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedMovement, setSelectedMovement] = useState(null);
    const [observacion, setObservacion] = useState("");
    const [categoria, setCategoria] = useState("");
    const [detalle, setDetalle] = useState("");
    const [periodoModal, setPeriodoModal] = useState("");
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [notaError, setNotaError] = useState("");
    const [estadoModal, setEstadoModal] = useState(""); // 'emitida' o 'error'
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        obtenerBoletas();
    }, [periodo]);

    const obtenerBoletas = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post("/financiero/boletas/lista", { periodo });
            setBoletas((response.data.data || []).map(withEstadoDerivado));
        } catch (error) {
            console.error("Error al obtener las boletas:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleSeleccionAuxiliar = (auxiliar) => {
        setAuxiliarInfo(auxiliar);
    };

    const handleAssignClick = (movement) => {
        setSelectedMovement(movement);
        setObservacion('');
        setCategoria('');
        setDetalle('');
        setPeriodoModal('');
        setAuxiliarInfo(null);
        setShowAssignModal(true);
    };

    const handleUploadFile = async () => {
        if (file) {
            const formData = new FormData();
            formData.append("file", file);
            try {
                await axiosInstance.post("/financiero/boletas/subir", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                alert("Archivo subido con éxito");
                setShowModal(false);
                obtenerBoletas();
            } catch (error) {
                console.error("Error al subir el archivo:", error);
                alert("Hubo un error al subir el archivo");
            }
        } else {
            alert("Por favor, seleccione un archivo para subir.");
        }
    };

    const handleGuardarEstado = async () => {
        const endpoint = "/financiero/boletas/asignar";
        const estadoBoleta = normalizeEstadoBoleta(estadoModal);

        const payload = {
            auxiliar_id: selectedMovement.auxiliar_id,
            nota: notaError,
            nota_boleta: notaError,
            notas_boleta: notaError,
            comentario: notaError,
            periodo,
            accion: estadoModal,
            estadoModal: estadoBoleta,
            estado: estadoBoleta,
            boleta_estado: estadoBoleta
        };

        gdAlertify.confirm({
            title: estadoModal === "emitida" ? "Confirmar Emisión" : "Confirmar Error",
            message: `¿Está seguro de marcar esta boleta como ${estadoModal}?`,
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    const response = await axiosInstance.post(endpoint, payload);

                    if (!response.data.error) {
                        gdAlertify.notiSuccess(`Boleta marcada como ${estadoModal}.`);
                        setShowErrorModal(false);
                        setBoletas((prev) => prev.map((boleta) => {
                            if (boleta.auxiliar_id !== selectedMovement.auxiliar_id) return boleta;
                            return withEstadoDerivado({
                                ...boleta,
                                boleta_estado: estadoBoleta,
                                notas_boleta: notaError
                            });
                        }));
                    } else {
                        gdAlertify.notiError(response.data.message || "Error al registrar.");
                    }
                } catch (error) {
                    gdAlertify.notiError("No se pudo conectar con el servidor.");
                } finally {
                    gdAlertify.hideLoader();
                }
            },
            onCancel: () => {
                gdAlertify.notify("Acción cancelada", "error");
            },
        });
    };

    const rawColumns = [
        {
            title: "Rut Paciente",
            name: "rut_paciente",
            render: row => getRutParts(row).rut
        },
        {
            title: "DV",
            name: "dv_paciente",
            render: row => getRutParts(row).dv
        },
        { title: "Nombre", name: "nombre_auxiliar", render: row => row.nombre_auxiliar || "" },
        { title: "Apellido Paterno", name: "apepat_auxiliar", render: row => row.apepat_auxiliar || "" },
        { title: "Apellido Materno", name: "apemat_auxiliar", render: row => row.apemat_auxiliar || "" },
        {
            title: "N° turnos",
            name: "cantidad_turnos_adelanto",
            cellClassName: "border-left",
            render: row => `${row.cantidad_turnos_adelanto || 0}`
        },
        {
            title: "Total turnos Bruto",
            name: "turnos_adelanto",
            render: row => `$${formatMoney(row.valor_turnos_adelanto || 0)}`
        },
        {
            title: "Horas Extras Bruto",
            name: "horas_extras_adelanto",
            render: row => `$${formatMoney(row.horas_extras_adelanto || 0)}`
        },
        {
            title: "Valor Boleta",
            name: "valor_boleta",
            render: row => {
                const bruto = (row.valor_turnos_total || row.valor_turnos_adelanto || 0) + (row.horas_extras_total || row.horas_extras_adelanto || 0);
                return (<span className="fw-bold">{`$${formatMoney(bruto)}`}</span>);
            }
        },
        {
            title: "Liquido Boleta",
            name: "liquido_a_145",
            render: row => {
                const bruto = (row.valor_turnos_total || row.valor_turnos_adelanto || 0) + (row.horas_extras_total || row.horas_extras_adelanto || 0);
                return `$${formatMoney(bruto * retencionSii)}`;
            }
        },
        {
            title: "Tiene Boleta Electrónica",
            name: "datos_sii",
            render: row => getTipoBoleta(row)
        },
        {
            title: "RUT Boleta",
            name: "rut_boleta",
            render: row => getRutBoleta(row)
        },
        {
            title: "Nombre Boleta",
            name: "nombre_boleta",
            render: row => getNombreBoleta(row)
        },
        {
            title: "Correo Auxiliar",
            name: "email_auxiliar",
            render: row => row.email_auxiliar || row.email || ""
        },
        {
            title: "Clave SII",
            name: "contrasena_sii",
            render: row => row.contrasena_sii || ""
        },
        {
            title: "Notas Boleta",
            name: "notas_boleta",
            render: row => row.notas_boleta || ""
        },
        {
            title: "Estado",
            name: "estado_label",
            render: row => {
                const color = row.estado_normalizado === 'aprobada' ? 'success' : row.estado_normalizado === 'rechazada' ? 'danger' : 'secondary';
                return <span className={`badge bg-${color}`}>{row.estado_label}</span>;
            }
        },
        {
            title: "Acciones",
            name: "acciones",
            render: row => (
                <>
                    <button
                        className="btn btn-sm btn-success me-1"
                        onClick={() => {
                            setSelectedMovement(row);
                            setNotaError(row.notas_boleta || row.nota_boleta || row.comentario || "");
                            setEstadoModal("emitida");
                            setShowErrorModal(true);
                        }}
                    >
                        Confirmar emisión
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                            setSelectedMovement(row);
                            setNotaError(row.notas_boleta || row.nota_boleta || row.comentario || "");
                            setEstadoModal("error");
                            setShowErrorModal(true);
                        }}
                    >
                        Marcar error
                    </button>
                </>
            )
        }
    ];

    const hiddenColumnNames = new Set([
        "cantidad_turnos_adelanto",
        "turnos_adelanto",
        "horas_extras_adelanto",
        "liquido_a_145"
    ]);

    const orderedColumnNames = [
        "rut_paciente",
        "dv_paciente",
        "nombre_auxiliar",
        "apepat_auxiliar",
        "apemat_auxiliar",
        "valor_boleta",
        "datos_sii",
        "rut_boleta",
        "nombre_boleta",
        "contrasena_sii",
        "email_auxiliar",
        "notas_boleta",
        "estado_label",
        "acciones"
    ];

    const columnsByName = new Map(rawColumns.map((column) => [column.name, column]));
    const columns = orderedColumnNames
        .map((name) => columnsByName.get(name))
        .filter((column) => column && !hiddenColumnNames.has(column.name))
        .map((column) => {
            if (column.name === "datos_sii") {
                return { ...column, title: "Datos Boleta" };
            }

            if (column.name === "contrasena_sii") {
                return { ...column, title: "Contraseña SII" };
            }

            if (column.name === "notas_boleta") {
                return {
                    ...column,
                    title: "Nota Boleta",
                    render: row => row.notas_boleta || row.nota_boleta || row.comentario || ""
                };
            }

            return column;
        });

    return (
        <div className="container mt-4">
            <div className="card p-4 shadow">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3>Boletas Emitidas</h3>
                    <div className="d-flex align-items-center">
                        <label className="form-label me-2">Período:</label>
                        <input
                            type="month"
                            className="form-control me-3"
                            value={periodo}
                            onChange={(e) => setPeriodo(e.target.value)}
                        />
                        <button className="btn btn-primary" onClick={obtenerBoletas} disabled={loading}>
                            {loading ? "Filtrando..." : "Filtrar"}
                        </button>
                        <button className="btn btn-secondary ms-3" onClick={() => setShowModal(true)}>
                            Subir archivo
                        </button>
                    </div>
                </div>

                <GdTable
                    columns={columns}
                    data={boletas}
                    stickyHeader={true}
                    height={800}
                    showCsvExport={true}
                    exportFilename="boletas-honorarios.xlsx"
                    initialSort={[0, "asc"]}
                    itemsPerPageOptions={[100, 200, 300, 500, 1000]}
                    defaultItemsPerPage={500}
                    loading={loading}
                />
            </div>

            {/* Modal para subir archivo */}
            {showModal && (
                <div className="modal fade show" style={{ display: "block" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Subir Archivo</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <input type="file" className="form-control" onChange={handleFileChange} />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cerrar</button>
                                <button className="btn btn-primary" onClick={handleUploadFile}>Subir</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal único para nota de emisión/error */}
            {showErrorModal && (
                <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{estadoModal === "emitida" ? "Confirmar Emisión" : "Marcar Boleta con Error"}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowErrorModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <label className="form-label">Nota:</label>
                                <input type="text" className="form-control" value={notaError} onChange={(e) => setNotaError(e.target.value)} />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowErrorModal(false)}>Cancelar</button>
                                <button className={`btn ${estadoModal === "emitida" ? "btn-success" : "btn-danger"}`} onClick={handleGuardarEstado}>Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BoletasHonorarios;
